import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';

import type { Form, FormField } from '../../interfaces/form.interface';
import type {
    DropdownData,
    FilteredValue,
    RepeatableDomainAdapterContext,
    RepeatableGroupConfiguration,
    RepeatableSubFieldDependencyContext,
    RepeatableGroupDomainAdapter,
    RepeatableInfoDialogView,
    RepeatableRowExtraView,
    RepeatableSubField,
    RepeatableSubFieldDataSourceContext,
} from '../../interfaces/input.interface';
import { InputType } from '../../interfaces/input.interface';
import { RepeatableComputeEngine } from './logic/repeatable-compute.engine';
import { RepeatableDomainAdapterFactory } from './logic/repeatable-domain-adapter';
import { RepeatableDropdownResolver } from './logic/repeatable-dropdown.resolver';
import type { PendingDropdownItem, RepeatableLogger, RepeatableRowValue } from './logic/repeatable-group.types';
import { RepeatableModelHydrator } from './logic/repeatable-model-hydrator';
import { RepeatablePendingDropdownResolver } from './logic/repeatable-pending-dropdown.resolver';
import { RepeatableReadinessService } from './logic/repeatable-readiness.service';
import { RepeatableRowFormFactory } from './logic/repeatable-row-form.factory';
import { RepeatableSubFieldDataSourceLoader } from './logic/repeatable-subfield-datasource.loader';
import { RepeatableValueUtils } from './logic/repeatable-value.utils';

type RepeatableRowChange = {
    rowIndex: number;
    key: string;
    previousValue: unknown;
    currentValue: unknown;
};

type RepeatableDependencyResolutionOptions = {
    preserveCurrentDependentValues?: boolean;
};

@Component({
    selector: 'app-repeatable-group',
    standalone: false,
    templateUrl: './repeatable-group.component.html',
    styleUrls: ['./repeatable-group.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepeatableGroupComponent<T> implements OnInit, OnDestroy, OnChanges {
    @Input({ required: true }) public field: FormField<T> | null = null;
    @Input({ required: true }) public model: FormGroup | null = null;
    @Input({ required: true }) public form: Form<T> | null = null;

    @Input() public isEditable = true;

    /** Enable detailed console diagnostics. */
    @Input() public debug = false;

    public readonly INPUT = InputType;

    public rowsArray: FormArray<FormGroup> = new FormArray<FormGroup>([]);

    public get formArrayName(): string {
        return this.field?.name?.trim() ?? '';
    }

    public infoDialogVisible = false;
    public infoDialogRowIndex: number | null = null;

    private readonly destroyed$ = new Subject<void>();
    private readonly modelBindingsStop$ = new Subject<void>();
    private readonly parentDependenciesStop$ = new Subject<void>();
    private readonly rowsArrayStop$ = new Subject<void>();
    private previousRowsSnapshot: RepeatableRowValue[] = [];
    private dependencyRunVersion = 0;

    private readonly logger: RepeatableLogger = {
        log: (message: string, data?: unknown): void => this.debugLog(message, data),
        warn: (message: string, data?: unknown): void => this.debugWarn(message, data),
    };

    private readonly dropdownResolver = new RepeatableDropdownResolver(
        this.logger,
        () => this.getRowsSnapshotAsStrings(),
        (rowIndex: number) => this.getRowSnapshot(rowIndex),
        () => this.model,
    );

    private readonly modelHydrator = new RepeatableModelHydrator(this.dropdownResolver, this.logger);
    private readonly pendingDropdownResolver = new RepeatablePendingDropdownResolver(this.dropdownResolver, this.logger);
    private readonly computeEngine = new RepeatableComputeEngine(this.logger);
    private readonly subFieldDataSourceLoader = new RepeatableSubFieldDataSourceLoader(this.logger);

    private domainAdapter: RepeatableGroupDomainAdapter | null = null;
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly repeatableReadiness = inject(RepeatableReadinessService);

    /** Avoid recursive loops while hydrating rows or applying silent patches. */
    private isInternalSync = false;

    private pendingDropdown: PendingDropdownItem[] = [];
    private isHydratingFromModel = false;
    private pendingResolveTimer: number | null = null;
    private pendingResolveAttempt = 0;

    private get cfg(): RepeatableGroupConfiguration {
        const configuration = this.field?.input?.repeatableGroupConfiguration;

        if (!configuration) {
            this.debugWarn('Repeatable group configuration is missing. Rendering empty safely.');

            return {
                rows: 1,
                addButtonLabel: 'Add',
                bindings: {},
                serialization: { separator: ';' },
                subFields: [],
            };
        }

        return configuration;
    }

    public ngOnInit(): void {
        this.refreshDomainAdapter();
        this.debugLog('ngOnInit() start', {
            isEditable: this.isEditable,
            bindings: this.cfg.bindings,
            serialization: this.cfg.serialization,
            subFields: (this.cfg.subFields ?? []).map((subField) => subField.key),
        });

        this.bootstrapFormSafe();
        this.bindModelHydrationPipeline();
        this.bindParentDependencyPipeline();
        this.hydrateFromModel();
        void this.loadInitialDataSources();
        this.bindRecomputePipeline();

        this.debugLog('ngOnInit() done');
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const modelChanged = Boolean(changes['model'] && !changes['model'].firstChange);
        const fieldChanged = Boolean(changes['field'] && !changes['field'].firstChange);

        if (!modelChanged && !fieldChanged) {
            return;
        }

        if (fieldChanged) {
            this.refreshDomainAdapter();
            this.subFieldDataSourceLoader.clear();
        }

        this.debugWarn('repeatable input changed -> rebuild rows binding + rehydrate', {
            modelChanged,
            fieldChanged,
        });
        this.rowsArrayStop$.next();
        this.bootstrapFormSafe();
        this.bindRecomputePipeline();
        this.bindModelHydrationPipeline();
        this.bindParentDependencyPipeline();
        this.hydrateFromModel();
        void this.loadInitialDataSources();
    }

    public ngOnDestroy(): void {
        this.debugLog('ngOnDestroy()');
        this.clearPendingResolveTimer();
        this.repeatableReadiness.clear(this.formArrayName);
        this.modelBindingsStop$.next();
        this.modelBindingsStop$.complete();
        this.parentDependenciesStop$.next();
        this.parentDependenciesStop$.complete();
        this.rowsArrayStop$.next();
        this.rowsArrayStop$.complete();
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    public visibleSubFields(): RepeatableSubField[] {
        return (this.cfg.subFields ?? []).filter((subField) => this.safeIsVisible(subField));
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public trackBySubFieldKey(index: number, subField: RepeatableSubField): string {
        return subField.key ?? String(index);
    }

    public get currentCount(): number {
        return this.rowsArray?.length ?? 0;
    }

    public get maxRows(): number {
        const max = Number(this.cfg.rows ?? 1);

        return Number.isFinite(max) && max > 0 ? max : 1;
    }

    public isStackedLayout(): boolean {
        return this.cfg.forceStackedLayout === true;
    }


    public canAddRow(): boolean {
        return this.isEditable && this.currentCount < this.maxRows;
    }

    public canDeleteRow(): boolean {
        return this.isEditable && this.currentCount > 1;
    }

    public getRowGroup(rowIndex: number): FormGroup {
        return this.rowsArray.at(rowIndex);
    }

    public addRow(): void {
        if (!this.canAddRow()) {
            return;
        }

        this.debugLog('addRow() before', { length: this.rowsArray.length });
        this.rowsArray.push(RepeatableRowFormFactory.createRowGroup(this.cfg.subFields ?? []));
        this.recomputeAllAndValidate('addRow');
        this.debugLog('addRow() after', { length: this.rowsArray.length });
    }

    public deleteRow(rowIndex: number): void {
        if (!this.isEditable || rowIndex < 0 || rowIndex >= this.currentCount) {
            return;
        }

        this.debugLog('deleteRow()', { rowIndex, beforeLength: this.rowsArray.length });
        this.rowsArray.removeAt(rowIndex);

        if (this.rowsArray.length === 0) {
            this.rowsArray.push(RepeatableRowFormFactory.createRowGroup(this.cfg.subFields ?? []));
        }

        this.recomputeAllAndValidate('deleteRow');
        this.debugLog('deleteRow() done', { afterLength: this.rowsArray.length });
    }

    public onlyTextCellText(subField: RepeatableSubField, rowIndex: number): string {
        const value = this.rowsArray.at(rowIndex)?.get(subField.key)?.value;

        if (value === null || value === undefined) {
            return '';
        }

        const normalizedValue = String(value).trim();

        return normalizedValue === '' || RepeatableValueUtils.isNullishStringToken(normalizedValue) ? '' : normalizedValue;
    }

    public readOnlyCellText(subField: RepeatableSubField, rowIndex: number): string {
        const value = this.rowsArray.at(rowIndex)?.get(subField.key)?.value;

        if (value === null || value === undefined) {
            return subField.input?.type === InputType.ONLY_TEXT ? '' : '-';
        }

        if (typeof value === 'string') {
            const trimmedValue = value.trim();

            if (trimmedValue === '' || RepeatableValueUtils.isNullishStringToken(trimmedValue)) {
                return subField.input?.type === InputType.ONLY_TEXT ? '' : '-';
            }

            if (this.isInfoLabelValue(subField, trimmedValue, rowIndex)) {
                return RepeatableValueUtils.normalizeLabelText(trimmedValue) || '-';
            }
        }

        switch (subField.input?.type) {
            case InputType.DROPDOWN:
            case InputType.DROPDOWN_SEARCH:
                return this.resolveDropdownLabel(subField, value, rowIndex) ?? '-';

            case InputType.MULTISELECT:
            case InputType.MULTISELECT_SEARCH:
                return this.resolveMultiLabels(subField, value, rowIndex) ?? '-';

            default:
                return String(value);
        }
    }

    public getNumericInputClasses(subField: RepeatableSubField, rowIndex: number): Record<string, boolean> {
        return this.getDomainAdapter().getNumericInputClasses?.(this.createDomainContext(rowIndex), subField.key) ?? {};
    }

    public rowExtraView(rowIndex: number): RepeatableRowExtraView {
        return this.getDomainAdapter().getRowExtraView?.(this.createDomainContext(rowIndex)) ?? {
            isVisible: false,
            items: [],
        };
    }

    public shouldShowRowExtra(rowIndex: number): boolean {
        return this.rowExtraView(rowIndex).isVisible;
    }

    public canShowInfoDialog(rowIndex: number): boolean {
        return this.getDomainAdapter().canOpenInfoDialog?.(this.createDomainContext(rowIndex)) ?? false;
    }

    public canShowRowActions(rowIndex: number): boolean {
        return this.isEditable || this.canShowInfoDialog(rowIndex);
    }

    public openInfoDialog(rowIndex: number): void {
        if (!this.canShowInfoDialog(rowIndex)) {
            return;
        }

        this.infoDialogRowIndex = rowIndex;
        this.infoDialogVisible = true;
    }

    public closeInfoDialog(): void {
        this.infoDialogVisible = false;
        this.infoDialogRowIndex = null;
    }

    public onInfoDialogVisibleChange(visible: boolean): void {
        if (!visible) {
            this.closeInfoDialog();
            return;
        }

        this.infoDialogVisible = true;
    }

    public get activeInfoDialogView(): RepeatableInfoDialogView | null {
        if (this.infoDialogRowIndex === null || this.infoDialogRowIndex < 0) {
            return null;
        }

        return this.getDomainAdapter().getInfoDialogView?.(this.createDomainContext(this.infoDialogRowIndex)) ?? null;
    }

    private publishReadiness(ready: boolean): void {
        const fieldName = this.formArrayName;

        if (fieldName === '') {
            return;
        }

        this.repeatableReadiness.setState({
            fieldName,
            ready,
            pendingDropdownCount: this.pendingDropdown.length,
            rowCount: this.rowsArray.length,
            updatedAt: Date.now(),
        });
    }

    public debugDump(): void {
        if (!this.debug) {
            return;
        }

        this.debugLog('debugDump()', {
            isEditable: this.isEditable,
            maxRows: this.maxRows,
            rows: this.getRowsSnapshot(),
            pendingDropdown: this.pendingDropdown,
            modelValue: this.model?.getRawValue?.() ?? null,
        });
    }

    public dropdownOptions(subField: RepeatableSubField, rowIndex: number): Array<FilteredValue<unknown>> {
        return this.dropdownResolver.options(subField, rowIndex);
    }

    public forceRehydrate(): void {
        this.hydrateFromModel();
        this.cdr.markForCheck();
    }


    private async loadInitialDataSources(): Promise<void> {
        const independentDataSourceFields = (this.cfg.subFields ?? [])
            .filter((subField) => subField.dataSource && this.getAllDependencyKeys(subField).length === 0);

        if (independentDataSourceFields.length === 0) {
            return;
        }

        const results = await Promise.all(independentDataSourceFields.map((subField) => this.loadSubFieldDataSource(subField, 0)));
        const didLoadAnyDataSource = results.some((didLoad) => didLoad);

        if (!didLoadAnyDataSource) {
            return;
        }

        this.hydrateFromModel();
        this.cdr.markForCheck();
    }

    private async loadSubFieldDataSource(subField: RepeatableSubField, rowIndex: number): Promise<boolean> {
        if (!subField.dataSource) {
            return false;
        }

        const data = await this.subFieldDataSourceLoader.load(subField, this.createDataSourceContext(rowIndex, subField));

        if (!data) {
            return false;
        }

        this.assignRepeatableDropdownData(subField, data);
        this.cdr.markForCheck();

        return true;
    }

    private assignRepeatableDropdownData(subField: RepeatableSubField, data: DropdownData<unknown>): void {
        const currentData = this.resolveMutableDropdownData(subField);

        currentData.values = [...data.values];
        currentData.filtered = [...data.filtered];
        if (data.label !== undefined) {
            currentData.label = data.label;
        }

        subField.input.data = currentData;
    }

    private resolveMutableDropdownData(subField: RepeatableSubField): DropdownData<unknown> {
        const currentData = subField.input?.data;

        if (this.isDropdownData(currentData)) {
            return currentData;
        }

        const fallbackData: DropdownData<unknown> = { values: [], filtered: [] };

        subField.input.data = fallbackData;

        return fallbackData;
    }

    private createDataSourceContext(rowIndex: number, subField: RepeatableSubField): RepeatableSubFieldDataSourceContext {
        return {
            rowIndex,
            row: this.getRowSnapshot(rowIndex),
            rows: this.getRowsSnapshotAsStrings(),
            model: this.model ?? new FormGroup({}),
            subField,
        };
    }

    private isDropdownData(value: unknown): value is DropdownData<unknown> {
        if (value === null || typeof value !== 'object') {
            return false;
        }

        const record = value as Partial<DropdownData<unknown>>;

        return Array.isArray(record.values) && Array.isArray(record.filtered);
    }

    private refreshDomainAdapter(): void {
        this.domainAdapter = RepeatableDomainAdapterFactory.create(this.cfg);
    }

    private getDomainAdapter(): RepeatableGroupDomainAdapter {
        if (!this.domainAdapter) {
            this.refreshDomainAdapter();
        }

        return this.domainAdapter as RepeatableGroupDomainAdapter;
    }

    private bootstrapFormSafe(): void {
        this.rowsArray = this.getOrCreateModelRowsArray();
    }

    private getOrCreateModelRowsArray(): FormArray<FormGroup> {
        const subFields = this.cfg.subFields ?? [];
        const fallbackRowsArray = (): FormArray<FormGroup> => RepeatableRowFormFactory.createRowsArray(subFields);

        if (!this.model || !this.formArrayName) {
            this.debugWarn('Repeatable group has no parent model or field name. Using isolated rows array.');
            return fallbackRowsArray();
        }

        const currentControl = this.model.get(this.formArrayName);

        if (currentControl instanceof FormArray) {
            const rowsArray = currentControl as FormArray<FormGroup>;

            if (rowsArray.length === 0) {
                rowsArray.push(RepeatableRowFormFactory.createRowGroup(subFields));
            }

            return rowsArray;
        }

        const rowsArray = fallbackRowsArray();

        this.model.setControl(this.formArrayName, rowsArray);
        this.debugWarn('Repeatable group model control was not a FormArray. It has been replaced safely.', {
            fieldName: this.formArrayName,
        });

        return rowsArray;
    }

    private bindModelHydrationPipeline(): void {
        if (!this.model) {
            this.debugWarn('bindModelHydrationPipeline(): model missing.');
            return;
        }

        const entries = Object.entries(this.cfg.bindings ?? {}) as Array<[string, string]>;

        if (entries.length === 0) {
            return;
        }

        this.modelBindingsStop$.next();

        for (const [logicalKey, targetControlName] of entries) {
            const control = this.model.get(targetControlName);

            if (!control) {
                this.debugWarn('Model control missing for binding.', { logicalKey, targetControlName });
                continue;
            }

            control.valueChanges
                .pipe(auditTime(0), takeUntil(this.destroyed$), takeUntil(this.modelBindingsStop$))
                .subscribe((value: unknown) => this.handleBoundModelChange(targetControlName, value));
        }
    }

    private handleBoundModelChange(targetControlName: string, value: unknown): void {
        if (this.isInternalSync || this.isHydratingFromModel) {
            return;
        }

        this.debugLog('External model change -> hydrateFromModel()', { targetControlName, value });
        this.hydrateFromModel();
    }

    private bindParentDependencyPipeline(): void {
        if (!this.model) {
            return;
        }

        this.parentDependenciesStop$.next();
        const dependencies = this.getParentDependencyKeys();

        for (const dependency of dependencies) {
            const control = this.model.get(dependency);

            if (!control) {
                continue;
            }

            control.valueChanges
                .pipe(auditTime(0), takeUntil(this.destroyed$), takeUntil(this.parentDependenciesStop$))
                .subscribe((value: unknown) => this.handleParentDependencyChange(dependency, value));
        }
    }

    private handleParentDependencyChange(dependency: string, value: unknown): void {
        if (this.isInternalSync || this.isHydratingFromModel) {
            return;
        }

        this.debugLog('Parent dependency changed -> hydrateFromModel()', { dependency, value });
        this.hydrateFromModel();
        void this.loadParentDependentDataSources();
    }

    private async loadParentDependentDataSources(): Promise<void> {
        const parentDependencies = this.getParentDependencyKeys();
        const dependentDataSourceFields = (this.cfg.subFields ?? [])
            .filter((subField) => subField.dataSource
                && this.getParentDependencyKeys(subField).some((key) => parentDependencies.includes(key)));

        if (dependentDataSourceFields.length === 0) {
            return;
        }

        const results = await Promise.all(dependentDataSourceFields.map((subField) => this.loadSubFieldDataSource(subField, 0)));

        if (results.some((didLoad) => didLoad)) {
            this.hydrateFromModel();
        }
    }

    private bindRecomputePipeline(): void {
        this.rowsArray.valueChanges
            .pipe(auditTime(0), takeUntil(this.destroyed$), takeUntil(this.rowsArrayStop$))
            .subscribe(() => {
                void this.handleRowsValueChange();
            });
    }

    private async handleRowsValueChange(): Promise<void> {
        if (this.isInternalSync) {
            return;
        }

        if (this.pendingDropdown.length > 0) {
            this.debugWarn(
                'rowsArray.valueChanges: dropping pendingDropdown because user edited a row.',
                { droppedCount: this.pendingDropdown.length, pending: this.pendingDropdown }
            );
            this.pendingDropdown = [];
            this.clearPendingResolveTimer();
            this.publishReadiness(true);
        }

        const runVersion = ++this.dependencyRunVersion;
        const changes = this.getRowValueChanges(this.previousRowsSnapshot, this.getRowsSnapshot());

        await this.resolveDeclaredDependencies(changes, runVersion);

        if (runVersion !== this.dependencyRunVersion || this.isInternalSync) {
            return;
        }

        this.debugLog('rowsArray.valueChanges -> recomputeAllAndValidate()', this.getRowsSnapshot());
        this.recomputeAllAndValidate('rowsArray.valueChanges');
        this.previousRowsSnapshot = this.getRowsSnapshot();
        this.publishReadiness(true);
        this.cdr.markForCheck();
    }

    private async resolveDeclaredDependencies(
        changes: RepeatableRowChange[],
        runVersion: number,
        options: RepeatableDependencyResolutionOptions = {},
    ): Promise<void> {
        if (changes.length === 0) {
            return;
        }

        for (const change of changes) {
            await this.resolveDeclaredDependency(change, runVersion, options);

            if (runVersion !== this.dependencyRunVersion) {
                return;
            }
        }
    }

    private async resolveDeclaredDependency(
        change: RepeatableRowChange,
        runVersion: number,
        options: RepeatableDependencyResolutionOptions = {},
    ): Promise<void> {
        const dependents = (this.cfg.subFields ?? [])
            .filter((subField) => this.getDependencyKeys(subField).includes(change.key));

        if (dependents.length === 0) {
            return;
        }

        const rowGroup = this.rowsArray.at(change.rowIndex);

        if (!rowGroup) {
            return;
        }

        const parentField = (this.cfg.subFields ?? []).find((subField) => subField.key === change.key) ?? null;

        for (const dependent of dependents) {
            const context = this.createDependencyContext(change.rowIndex, dependent, parentField, change);

            this.clearDependentControlIfNeeded(rowGroup, dependent, context, options);

            if (dependent.dataSource) {
                await this.loadSubFieldDataSource(dependent, change.rowIndex);

                if (runVersion !== this.dependencyRunVersion) {
                    return;
                }
            }

            if (typeof dependent.loadData === 'function') {
                const loadedData = await dependent.loadData(context);

                if (runVersion !== this.dependencyRunVersion) {
                    return;
                }

                if (loadedData) {
                    this.assignRepeatableDropdownData(dependent, loadedData);
                    this.cdr.markForCheck();
                }
            }

            await this.patchDependencyChange(rowGroup, dependent, context, runVersion);

            if (this.autoSelectSingleOption(rowGroup, dependent, change.rowIndex)) {
                await this.resolveDeclaredDependency({
                    rowIndex: change.rowIndex,
                    key: dependent.key,
                    previousValue: null,
                    currentValue: rowGroup.get(dependent.key)?.value,
                }, runVersion, options);
            }
        }
    }

    private clearDependentControlIfNeeded(
        rowGroup: FormGroup,
        dependent: RepeatableSubField,
        context: RepeatableSubFieldDependencyContext,
        options: RepeatableDependencyResolutionOptions,
    ): void {
        const control = rowGroup.get(dependent.key);

        if (!control || dependent.dependencyBehaviour?.clearOnParentChange === false) {
            return;
        }

        if (options.preserveCurrentDependentValues && RepeatableValueUtils.hasNonEmptyValue(control.value)) {
            return;
        }

        const shouldKeepCurrentValue = dependent.dependencyBehaviour?.keepCurrentValueIfStillAvailable === true &&
            this.isCurrentValueAvailable(dependent, context.rowIndex, control.value);

        if (shouldKeepCurrentValue) {
            return;
        }

        control.patchValue(this.getClearedRepeatableValue(dependent), { emitEvent: false });
        control.markAsPristine();
        control.markAsUntouched();
    }

    private async patchDependencyChange(
        rowGroup: FormGroup,
        dependent: RepeatableSubField,
        context: RepeatableSubFieldDependencyContext,
        runVersion: number,
    ): Promise<void> {
        if (typeof dependent.onDependencyChange !== 'function') {
            return;
        }

        const patch = await dependent.onDependencyChange(context);

        if (runVersion !== this.dependencyRunVersion || !patch) {
            return;
        }

        this.patchRowSilently(rowGroup, patch);
    }

    private autoSelectSingleOption(rowGroup: FormGroup, dependent: RepeatableSubField, rowIndex: number): boolean {
        if (dependent.dependencyBehaviour?.autoSelect !== 'singleOption') {
            return false;
        }

        const control = rowGroup.get(dependent.key);

        if (!control || RepeatableValueUtils.hasNonEmptyValue(control.value)) {
            return false;
        }

        const options = this.dropdownOptions(dependent, rowIndex)
            .filter((option) => option?.value !== null && option?.value !== undefined);

        if (options.length !== 1) {
            return false;
        }

        control.patchValue(options[0]?.value ?? null, { emitEvent: false });
        control.markAsDirty();
        control.markAsTouched();

        return true;
    }

    private patchRowSilently(rowGroup: FormGroup, patch: Record<string, unknown>): void {
        this.isInternalSync = true;

        try {
            for (const [key, value] of Object.entries(patch)) {
                const control = rowGroup.get(key);

                if (!control) {
                    continue;
                }

                control.patchValue(value, { emitEvent: false });
                control.markAsDirty();
                control.markAsTouched();
            }
        } finally {
            this.isInternalSync = false;
        }
    }

    private createDependencyContext(
        rowIndex: number,
        dependent: RepeatableSubField,
        parentField: RepeatableSubField | null,
        change: RepeatableRowChange,
    ): RepeatableSubFieldDependencyContext {
        return {
            rowIndex,
            row: this.getRowSnapshot(rowIndex),
            rows: this.getRowsSnapshotAsStrings(),
            model: this.model ?? new FormGroup({}),
            changedKey: change.key,
            changedValue: change.currentValue,
            subField: dependent,
            parentField,
            resolveEntity: <TEntity extends Record<string, unknown>>(key: string): TEntity | null =>
                this.resolveSelectedEntity<TEntity>(rowIndex, key),
        };
    }

    private getRowValueChanges(previousRows: RepeatableRowValue[], currentRows: RepeatableRowValue[]): RepeatableRowChange[] {
        const changes: RepeatableRowChange[] = [];
        const maxRows = Math.max(previousRows.length, currentRows.length);

        for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
            const previousRow = previousRows[rowIndex] ?? {};
            const currentRow = currentRows[rowIndex] ?? {};
            const keys = new Set([...Object.keys(previousRow), ...Object.keys(currentRow)]);

            for (const key of keys) {
                const previousValue = previousRow[key];
                const currentValue = currentRow[key];

                if (!RepeatableValueUtils.areEquivalentCellValues(previousValue, currentValue)) {
                    changes.push({ rowIndex, key, previousValue, currentValue });
                }
            }
        }

        return changes;
    }

    private getDependencyKeys(subField: RepeatableSubField): string[] {
        return this.toStringArray(subField.dependsOn);
    }

    private getParentDependencyKeys(subField?: RepeatableSubField): string[] {
        const keys = new Set<string>([
            ...this.toStringArray(this.cfg.parentDependencies),
            ...this.toStringArray(this.field?.vinculatedFields),
            ...this.toStringArray(subField?.parentDependencies),
        ].map((key) => key.trim()).filter((key) => key !== ''));

        return Array.from(keys);
    }

    private getAllDependencyKeys(subField: RepeatableSubField): string[] {
        return [...this.getDependencyKeys(subField), ...this.getParentDependencyKeys(subField)];
    }

    private toStringArray(value: string | readonly string[] | undefined): string[] {
        if (!value) {
            return [];
        }

        return typeof value === 'string' ? [value] : [...value];
    }

    private isCurrentValueAvailable(subField: RepeatableSubField, rowIndex: number, currentValue: unknown): boolean {
        if (!RepeatableValueUtils.hasNonEmptyValue(currentValue)) {
            return false;
        }

        const primaryKey = this.getRepeatableControlValueKey(subField);
        const selectedIds = RepeatableValueUtils.parseIdsFromToken(currentValue, ',', primaryKey);
        const availableIds = new Set(
            this.dropdownOptions(subField, rowIndex)
                .map((option) => RepeatableValueUtils.getOptionKey(option?.value, primaryKey))
                .filter((id) => id !== ''),
        );

        return selectedIds.length > 0 && selectedIds.every((id) => availableIds.has(id));
    }


    private getRepeatableControlValueKey(subField: RepeatableSubField): string {
        return subField.input?.dropdownConfiguration?.controlValueKey
            ?? subField.input?.dropdownConfiguration?.valuePrimaryKey
            ?? 'id';
    }

    private getClearedRepeatableValue(subField: RepeatableSubField): unknown {
        return RepeatableValueUtils.isMultiSelectType(subField.input?.type) ? [] : null;
    }

    private hydrateFromModel(): void {
        this.pendingDropdown = [];
        this.pendingResolveAttempt = 0;
        this.publishReadiness(false);
        this.isHydratingFromModel = true;
        this.isInternalSync = true;

        try {
            const result = this.modelHydrator.hydrateFromModel({
                model: this.model,
                rowsArray: this.rowsArray,
                configuration: this.cfg,
                isEditable: this.isEditable,
                maxRows: this.maxRows,
            });

            this.pendingDropdown = result.pendingDropdown;
        } finally {
            this.isInternalSync = false;
            this.isHydratingFromModel = false;
        }

        this.recomputeAll('hydrateFromModel');
        this.syncRowsValidationToModel();
        this.previousRowsSnapshot = this.getRowsSnapshot();
        this.cdr.markForCheck();

        if (this.pendingDropdown.length > 0) {
            this.debugWarn('hydrateFromModel(): pendingDropdown (options not ready or ID filtered out).', {
                pendingCount: this.pendingDropdown.length,
                pending: this.pendingDropdown,
            });
            this.publishReadiness(false);
            void this.resolveInitialDependenciesForHydratedRows();
            this.scheduleResolvePending();
            return;
        }

        this.publishReadiness(this.pendingDropdown.length === 0);
        void this.resolveInitialDependenciesForHydratedRows();
    }

    private async resolveInitialDependenciesForHydratedRows(): Promise<void> {
        const changes = this.getInitialDependencyChangesForCurrentRows();

        if (changes.length === 0) {
            return;
        }

        const runVersion = ++this.dependencyRunVersion;

        await this.resolveDeclaredDependencies(changes, runVersion, {
            preserveCurrentDependentValues: true,
        });

        if (runVersion !== this.dependencyRunVersion) {
            return;
        }

        if (this.pendingDropdown.length > 0) {
            const result = this.pendingDropdownResolver.resolvePending(this.rowsArray, this.pendingDropdown);

            this.pendingDropdown = result.stillPending;
        }

        this.recomputeAll('initialDependencyHydration');

        if (this.pendingDropdown.length === 0) {
            this.previousRowsSnapshot = this.getRowsSnapshot();
        }

        this.publishReadiness(this.pendingDropdown.length === 0);
        this.cdr.markForCheck();
    }

    private getInitialDependencyChangesForCurrentRows(): RepeatableRowChange[] {
        const dependencyKeys = new Set<string>();

        for (const subField of this.cfg.subFields ?? []) {
            for (const dependencyKey of this.getDependencyKeys(subField)) {
                dependencyKeys.add(dependencyKey);
            }
        }

        if (dependencyKeys.size === 0) {
            return [];
        }

        return this.getRowsSnapshot()
            .flatMap((row, rowIndex) => Array.from(dependencyKeys).map((key) => ({
                rowIndex,
                key,
                previousValue: null,
                currentValue: row[key],
            })))
            .filter((change) => RepeatableValueUtils.hasNonEmptyValue(change.currentValue));
    }

    private recomputeAllAndValidate(source: string): void {
        this.recomputeAll(source);
        this.syncRowsValidationToModel();
    }

    private recomputeAll(source: string): void {
        this.computeEngine.recomputeAll(source, this.rowsArray, this.cfg.subFields ?? [], this.model);
    }

    private syncRowsValidationToModel(): void {
        const fieldControlName = this.field?.name ?? '';
        const fieldControl = fieldControlName ? this.model?.get(fieldControlName) : null;

        if (!fieldControl) {
            return;
        }

        const currentErrors = { ...(fieldControl.errors ?? {}) };

        delete currentErrors['repeatableRowInvalid'];

        const hasInvalidRows = this.rowsArray.controls.some((control) => control.invalid);

        if (hasInvalidRows) {
            currentErrors['repeatableRowInvalid'] = 'El grupo contiene valores inválidos. Revisa las filas marcadas.';
        }

        const nextErrors = Object.keys(currentErrors).length > 0 ? currentErrors : null;

        fieldControl.setErrors(nextErrors);
    }

    private scheduleResolvePending(): void {
        this.clearPendingResolveTimer();
        this.publishReadiness(false);

        this.pendingResolveTimer = window.setTimeout(() => {
            void this.tryResolvePending();
        }, 50);

        this.debugLog('scheduleResolvePending() setTimeout 50ms');
    }

    private async tryResolvePending(): Promise<void> {
        if (this.pendingDropdown.length === 0) {
            this.publishReadiness(true);
            return;
        }

        const maxAttempts = 200;

        this.pendingResolveAttempt++;

        const result = this.pendingDropdownResolver.resolvePending(this.rowsArray, this.pendingDropdown);

        this.pendingDropdown = result.stillPending;

        this.debugLog('tryResolvePending(): status', {
            attempt: this.pendingResolveAttempt,
            stillPendingCount: this.pendingDropdown.length,
            stillPending: this.pendingDropdown,
            didResolveAny: result.didResolveAny,
            resolvedChanges: result.resolvedChanges,
            rows: this.getRowsSnapshot(),
            modelValue: this.model?.getRawValue?.() ?? null,
        });

        let resolvedChanges = result.resolvedChanges;
        let cascadePass = 0;

        while (resolvedChanges.length > 0 && cascadePass < maxAttempts) {
            cascadePass++;

            const runVersion = ++this.dependencyRunVersion;

            await this.resolveDeclaredDependencies(
                resolvedChanges.map((change) => ({
                    rowIndex: change.rowIndex,
                    key: change.logicalKey,
                    previousValue: change.previousValue,
                    currentValue: change.currentValue,
                })),
                runVersion,
                { preserveCurrentDependentValues: true },
            );

            if (runVersion !== this.dependencyRunVersion) {
                return;
            }

            const retryResult = this.pendingDropdownResolver.resolvePending(this.rowsArray, this.pendingDropdown);

            this.pendingDropdown = retryResult.stillPending;
            resolvedChanges = retryResult.resolvedChanges;
        }

        if (this.pendingDropdown.length > 0 && this.pendingResolveAttempt < maxAttempts) {
            this.scheduleResolvePending();
            return;
        }

        this.recomputeAll('tryResolvePending');

        if (this.pendingDropdown.length > 0) {
            this.debugWarn('tryResolvePending(): unresolved dropdowns remain after max attempts. Keeping current rows untouched.', {
                pendingCount: this.pendingDropdown.length,
                pending: this.pendingDropdown,
            });
            this.publishReadiness(false);
            this.cdr.markForCheck();
            return;
        }

        this.syncRowsValidationToModel();
        this.previousRowsSnapshot = this.getRowsSnapshot();
        this.publishReadiness(true);
        this.cdr.markForCheck();
    }

    private clearPendingResolveTimer(): void {
        if (this.pendingResolveTimer !== null) {
            clearTimeout(this.pendingResolveTimer);
            this.pendingResolveTimer = null;
        }
    }

    private createDomainContext(rowIndex: number): RepeatableDomainAdapterContext {
        return {
            rowIndex,
            row: this.getRowSnapshot(rowIndex),
            rows: this.getRowsSnapshotAsStrings(),
            model: this.model,
            subFields: this.cfg.subFields ?? [],
            resolveEntity: <TEntity extends Record<string, unknown>>(key: string): TEntity | null =>
                this.resolveSelectedEntity<TEntity>(rowIndex, key),
        };
    }

    private resolveSelectedEntity<TEntity extends Record<string, unknown>>(rowIndex: number, key: string): TEntity | null {
        const row = this.getRowSnapshot(rowIndex);
        const value = row[key];

        if (value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value)) {
            return value as TEntity;
        }

        const subField = (this.cfg.subFields ?? []).find((item) => item.key === key);

        if (!subField) {
            return null;
        }

        return this.dropdownResolver.mapIdToEntity<TEntity>(value, subField, rowIndex);
    }

    private isInfoLabelValue(subField: RepeatableSubField, raw: unknown, rowIndex: number): boolean {
        if (this.isEditable) {
            return false;
        }

        const type = subField.input?.type;
        const isSelect = RepeatableValueUtils.isSelectType(type);

        if (!isSelect || raw === null || raw === undefined || typeof raw === 'object') {
            return false;
        }

        const stringValue = String(raw).trim();

        if (stringValue === '' || RepeatableValueUtils.isNullishStringToken(stringValue)) {
            return false;
        }

        const parts = stringValue
            .split(';')
            .map((part) => part.trim())
            .filter((part) => part !== '');

        if (parts.length === 0 || parts.every((part) => RepeatableValueUtils.isNumericLike(part))) {
            return false;
        }

        const primaryKey = this.getRepeatableControlValueKey(subField);
        const options = this.dropdownOptions(subField, rowIndex);

        if (options.length > 0) {
            return !parts.some((part) => options.some((option) => RepeatableValueUtils.getOptionKey(option?.value, primaryKey) === part));
        }

        return true;
    }

    private resolveDropdownLabel(subField: RepeatableSubField, raw: unknown, rowIndex: number): string | null {
        const primaryKey = this.getRepeatableControlValueKey(subField);
        const ids = RepeatableValueUtils.parseIdsFromToken(raw, ';', primaryKey);

        if (ids.length === 0) {
            return null;
        }

        const labels = this.dropdownResolver.idsToLabels(subField, ids, rowIndex);

        return labels.length > 0 ? labels.join(', ') : null;
    }

    private resolveMultiLabels(subField: RepeatableSubField, raw: unknown, rowIndex: number): string | null {
        const primaryKey = this.getRepeatableControlValueKey(subField);
        const ids = RepeatableValueUtils.parseIdsFromToken(raw, ';', primaryKey);

        if (ids.length === 0) {
            return null;
        }

        const labels = this.dropdownResolver.idsToLabels(subField, ids, rowIndex);

        return labels.length > 0 ? labels.join(', ') : null;
    }

    private safeIsVisible(subField: RepeatableSubField): boolean {
        if (typeof subField.isVisible !== 'function') {
            return true;
        }

        try {
            return subField.isVisible() !== false;
        } catch (error: unknown) {
            this.debugWarn('isVisible() threw; showing field by default.', { key: subField.key, error });
            return true;
        }
    }

    private getRowSnapshot(rowIndex: number): RepeatableRowValue {
        return (this.rowsArray.at(rowIndex)?.getRawValue() as RepeatableRowValue | undefined) ?? {};
    }

    private getRowsSnapshot(): RepeatableRowValue[] {
        return RepeatableValueUtils.getRowsSnapshot(this.rowsArray);
    }

    private getRowsSnapshotAsStrings(): Array<Record<string, string>> {
        return RepeatableValueUtils.getRowsSnapshotAsStrings(this.rowsArray, this.cfg.subFields ?? []);
    }

    private debugLog(message: string, data?: unknown): void {
        if (!this.debug) {
            return;
        }

        console.log(`[RepeatableGroup] ${message}`, data ?? '');
    }

    private debugWarn(message: string, data?: unknown): void {
        if (!this.debug) {
            return;
        }

        console.warn(`[RepeatableGroup] ${message}`, data ?? '');
    }
}
